import {
  PropsWithChildren, useEffect, createRef, RefObject, KeyboardEvent,
} from 'react';
import { slide as Menu } from 'react-burger-menu';
import FocusLock, { MoveFocusInside } from 'react-focus-lock';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';

import x from '../images/close-x-red.svg';
import { fixPropWarnings } from '../utils/fixPropWarnings';
import { BladeType, useBlade } from '../state/BladeProvider';
import { ModalData, ModalType } from '../state/ModalProvider';

import Portal from './Portal';
import CallToAction from './CallToAction';
import ModalTrigger from './ModalTrigger';
import GetAQuoteButtonOrLink from './GetAQuoteRenewalCTA';

interface StyleProps{
  isOpen: boolean,
  ref?: RefObject<HTMLDivElement>
}

interface BladeProps {
  isOpen:boolean,
  closeMsg: string,
  title: string,
  action: () => void,
  closeAllMenusOnEsc: (e: KeyboardEvent) => void,
  resetState: () => void,
  ctas?: {
    id: string,
    buttonLabel: string,
    buttonType: string,
    buttonUrl: string,
    internal: {
      type: string
    }
    button: {
      buttonType: string
    }
    bladeType: string,
    title: string,
    modalType: ModalType
  }[]
}

// mirroring HeaderButtonStyles src/components/styles.ts to match sibling button styles
// refactor this when there is more time?
export const BladeCallToAction = styled(CallToAction)`
    &:lang(fr) {
      line-height: 15px;
      min-height: 42px;
      max-height: 42px;
    }
`;

export const Overlay = styled.div.withConfig(fixPropWarnings('isOpen'))<StyleProps>`
  position: fixed;
  z-index: 1000;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5) none repeat scroll 0% 0%;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition: opacity 0.3s ease 0s;
  transition: ${({ isOpen }) => isOpen
    ? 'opacity 0.3s ease 0s'
    : 'opacity 0.3s ease 0s, transform 0s ease 0.3s'};
  transform: ${({ isOpen }) => (isOpen ? '' : 'translate3d(100%, 0px, 0px)')};
  top: 0px;
  left: 0px;
  right: 0px;

  &.Make-a-claim,
  &.Get-a-quote {
    z-index: 1001;
  }

  &.Get-a-quote {
      background: rgba(0, 0, 0, 0.1) none repeat scroll 0% 0%;
  }
`;

export const Wrapper = styled.div.withConfig(fixPropWarnings('isOpen'))<StyleProps>`
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
`;

export const Header = styled.div`
  width: 100%;
  outline: none;
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  color: ${themeGet('colors.red', 'red')};
  padding: 1.5rem 1rem 1.5rem;
  border-bottom: 2px solid ${themeGet('colors.greys.1')};

  p,
  h2 {
    margin: 0;
    font-size: 16px;
    letter-spacing: -0.06px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding: 1.5rem 2rem 1.5rem 2rem;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding: 1.5rem 2rem 1.5rem 3rem;
  }
`;

export const CloseBtn = styled.button`
  font-size: 16px;
  letter-spacing: -0.06px;
  display: flex;
  align-items: center;
  color: transparent;

  &::after {
    content: '';
    display: block;
    color: red;
    height: 17px;
    width: 17px;
    margin-left: 1rem;
    background-image: url(${x});
    background-repeat: no-repeat;
    background-size: cover;
  }

  @media only screen and (min-width: 679px) {
    color: ${themeGet('colors.red', 'red')};
  }
`;

export const Body = styled.div`
  padding: 20px 1rem;
  outline: none;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding: 2rem;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding: 3rem;
  }
`;

const MenuCTAs = styled.div`
  display: flex;
  gap: 20px;
`;

const Blade = ({
  children,
  title = 'Menu',
  closeMsg,
  isOpen,
  action,
  resetState,
  closeAllMenusOnEsc,
  ctas,
}: PropsWithChildren<BladeProps>) => {
  const modalRef = createRef<HTMLDivElement>();
  const { setActiveBlade } = useBlade();
  useEffect(() => {
    if (isOpen === true) {
      // Create array of focusable elements within ref once the blade is mounted
      const focusableModalElements = modalRef.current?.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select',
      );

      // Set the focusable element to the first element within the referenced array
      const firstElement = focusableModalElements?.[0];

      // If the blade is open, autofocus on first element
      // Timeout added to account for animation of blade as content is set to `visibility: hidden` via css to prevent screen readers from reading the hidden content.

      setTimeout(() => {
        (firstElement as HTMLElement)?.focus();
      }, 500);
    }
  }, [isOpen, modalRef]);

  const safeId = (string: string) => string
    .replace(/[^\w\s]/g, '')
    .split(' ')
    .join('-');

  return (
    <Portal id="blade-menu">
      {/* Put wrapper in FocusLock and focus will be moved inside on mount, then return focus into initial position on unmount */}
      <FocusLock disabled={!isOpen} returnFocus>
        <Wrapper
          id={`outer-container-${safeId(title)}`}
          isOpen={isOpen}
          ref={modalRef}
        >
          <Overlay
            className={safeId(title)}
            data-testid="blade-overlay"
            isOpen={isOpen}
            onClick={() => resetState()}
          />
          <Menu
            right
            noOverlay
            id={`outer-container-${safeId(title)}`}
            htmlClassName="blade-is-open"
            className={isOpen ? `is-active` : ''}
            customBurgerIcon={false}
            customCrossIcon={false}
            isOpen={isOpen}
            customOnKeyDown={closeAllMenusOnEsc}
            outerContainerId={`outer-container-${safeId(title)}`}
          >
            {/* Move focus inside of FocusLock element */}
            <MoveFocusInside>
              <Header>
                <MenuCTAs>
                  {ctas?.map((cta) => {
                    switch (cta?.internal?.type) {
                      case 'ContentfulButton':
                      case 'ContentfulLinkWrapper':
                        return (
                          <GetAQuoteButtonOrLink
                            key={cta.id}
                            cta={cta}
                            CtaComponent={BladeCallToAction}
                          />
                        );

                      case 'ContentfulBlade':
                        return (cta.bladeType === 'get-a-quote') && (
                        <CallToAction
                          key={cta.id}
                          variant={cta.button.buttonType as string}
                          onClick={() => setActiveBlade(BladeType.QUOTE)}
                        >
                          {cta.title}
                        </CallToAction>
                        );
                      case 'ContentfulModal':
                        return (
                          <ModalTrigger
                            data={cta as unknown as ModalData}
                            key={cta.id}
                            onClick={() => setActiveBlade(null)}
                          />
                        );
                      default:
                        return null;
                    }
                  })}
                </MenuCTAs>
                <CloseBtn
                  type="button"
                  aria-label={closeMsg}
                  onClick={() => action()}
                />
              </Header>
            </MoveFocusInside>
            <Body>{children}</Body>
          </Menu>
        </Wrapper>
      </FocusLock>
    </Portal>
  );
};

export default Blade;
